export const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

export const readFileAsDataUrl = (file) =>
	new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.onload = () => resolve(String(reader.result || ''))
		reader.onerror = () => reject(new Error('Failed to read image file'))
		reader.readAsDataURL(file)
	})

const loadImageWithCrossOrigin = (sourceUrl, crossOriginMode) =>
	new Promise((resolve, reject) => {
		const image = new Image()
		if (crossOriginMode) {
			image.crossOrigin = crossOriginMode
		}
		image.onload = () => resolve(image)
		image.onerror = () => reject(new Error('Could not load selected image'))
		image.src = sourceUrl
	})

const isRemoteUrl = (sourceUrl) => {
	try {
		const parsed = new URL(sourceUrl, window.location.href)
		return parsed.protocol === 'http:' || parsed.protocol === 'https:'
	} catch {
		return false
	}
}

export const loadImage = async (sourceUrl) => {
	if (!isRemoteUrl(sourceUrl)) {
		return loadImageWithCrossOrigin(sourceUrl)
	}

	try {
		return await loadImageWithCrossOrigin(sourceUrl, 'anonymous')
	} catch {
		// Fallback allows displaying remote images that do not expose CORS headers.
		// Cropping such images can still fail at export with a security error.
		return loadImageWithCrossOrigin(sourceUrl)
	}
}

export const getCropGeometry = ({
	imageWidth,
	imageHeight,
	viewportWidth,
	viewportHeight,
	zoom = 1,
	pan = { x: 0, y: 0 },
}) => {
	if (
		!imageWidth ||
		!imageHeight ||
		!viewportWidth ||
		!viewportHeight ||
		imageWidth <= 0 ||
		imageHeight <= 0
	) {
		return null
	}

	const safeZoom = clamp(Number(zoom) || 1, 1, 3)
	const baseScale = Math.max(viewportWidth / imageWidth, viewportHeight / imageHeight)
	const scale = baseScale * safeZoom
	const displayWidth = imageWidth * scale
	const displayHeight = imageHeight * scale
	const maxPanX = Math.max(0, (displayWidth - viewportWidth) / 2)
	const maxPanY = Math.max(0, (displayHeight - viewportHeight) / 2)
	const panX = clamp(Number(pan?.x) || 0, -maxPanX, maxPanX)
	const panY = clamp(Number(pan?.y) || 0, -maxPanY, maxPanY)

	const sourceWidth = viewportWidth / scale
	const sourceHeight = viewportHeight / scale
	const sourceCenterX = imageWidth / 2 - panX / scale
	const sourceCenterY = imageHeight / 2 - panY / scale
	const sourceX = clamp(sourceCenterX - sourceWidth / 2, 0, imageWidth - sourceWidth)
	const sourceY = clamp(sourceCenterY - sourceHeight / 2, 0, imageHeight - sourceHeight)

	return {
		zoom: safeZoom,
		scale,
		displayWidth,
		displayHeight,
		panX,
		panY,
		maxPanX,
		maxPanY,
		sourceX,
		sourceY,
		sourceWidth,
		sourceHeight,
	}
}

export const cropImageToBlob = async ({
	sourceUrl,
	targetWidth,
	targetHeight,
	zoom,
	pan,
}) => {
	const crossOriginErrorMessage =
		'This image cannot be cropped due to browser security rules. Please upload the original image file again.'
	const image = await loadImage(sourceUrl)
	const imageWidth = image.naturalWidth || image.width
	const imageHeight = image.naturalHeight || image.height
	const geometry = getCropGeometry({
		imageWidth,
		imageHeight,
		viewportWidth: targetWidth,
		viewportHeight: targetHeight,
		zoom,
		pan,
	})
	if (!geometry) throw new Error('Could not initialize image cropper')

	const canvas = document.createElement('canvas')
	canvas.width = targetWidth
	canvas.height = targetHeight
	const context = canvas.getContext('2d')
	if (!context) throw new Error('Could not initialize image cropper')

	try {
		context.drawImage(
			image,
			geometry.sourceX,
			geometry.sourceY,
			geometry.sourceWidth,
			geometry.sourceHeight,
			0,
			0,
			targetWidth,
			targetHeight,
		)
	} catch (error) {
		if (error?.name === 'SecurityError') {
			throw new Error(crossOriginErrorMessage)
		}
		throw error
	}

	return new Promise((resolve, reject) => {
		try {
			canvas.toBlob(
				(blob) => (blob ? resolve(blob) : reject(new Error('Could not crop image'))),
				'image/jpeg',
				0.92,
			)
		} catch (error) {
			if (error?.name === 'SecurityError') {
				reject(new Error(crossOriginErrorMessage))
				return
			}
			reject(error)
		}
	})
}
